/* ============================================================================
   VILLAINS TCG — Mystery Bags interactions (cst-mystery.js)
   Two real, self-contained front-end interactions (no backend, nothing faked):
   • Demo-pull pack — tap a hero pack to reveal a random editor-configured demo
     card, "Open another" to re-roll. Consecutive re-rolls never repeat the same
     card back-to-back, so the demo always feels like a fresh pull.
   • Buyback estimator — market value × selected tier rate → "you'd receive",
     formatted in the store currency. Pure math.
   Progressive enhancement: with JS off the pack shows its hint and the estimator
   is inert (no misleading output). Re-inits on shopify:section:load.

   NOTE: the opening flourish is CSS, toggled via the .is-opening class before the
   reveal. Each tier has its own sequence and its own length — the length comes
   from data-cst-open-ms on the pack, so Liquid, CSS and JS all agree on one
   number. To revert a tier to an instant reveal, set that attribute to 0.

   The after-open hint text comes from the pack's data-cst-reveal-hint attribute
   (set in the theme editor). It must describe the REAL flow: a pull is opened and
   the buyer keeps/ships the item OR sells it back for store credit — bags are NOT
   shipped sealed.
   ========================================================================== */
(() => {
  'use strict';

  const money = (v, currency) => {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(v); }
    catch (_) { return (currency ? currency + ' ' : '$') + (Math.round(v * 100) / 100).toFixed(2); }
  };

  // fallback when a pack doesn't declare its own; 0 (or reduced-motion) => instant
  const OPEN_ANIM_MS = 420;
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  // accurate fallback if the editor field is empty (never says "ship sealed")
  const DEFAULT_REVEAL_HINT = 'Demo pull — on a real pull, ship your hit or sell it back for store credit.';

  /* --------------------------------------------------------------- 3D tilt
     The pack used to be a flat rectangle. It now leans toward the pointer, which
     (with the CSS holo + shine) is what makes it feel like a real collectible
     rather than a placeholder. Pure presentation: nothing here affects the pull.
     Disabled for reduced-motion and for coarse pointers (touch), where tilt just
     fights the tap. To remove, delete initTilt and its call in scan(). */
  const TILT_MAX = 11; // degrees
  const initTilt = (face) => {
    if (!face || face.dataset.cstTilt) return;
    face.dataset.cstTilt = '1';
    if (reduceMotion() || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    let raf = 0;
    const move = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const r = face.getBoundingClientRect();
        if (!r.width || !r.height) return;
        const px = (e.clientX - r.left) / r.width - 0.5;  // -0.5 … 0.5
        const py = (e.clientY - r.top) / r.height - 0.5;
        face.style.setProperty('--cst-ry', (px * TILT_MAX * 2).toFixed(2) + 'deg');
        face.style.setProperty('--cst-rx', (-py * TILT_MAX * 2).toFixed(2) + 'deg');
      });
    };
    const enter = () => face.classList.add('is-tilting');
    const leave = () => {
      face.classList.remove('is-tilting');
      face.style.removeProperty('--cst-rx');
      face.style.removeProperty('--cst-ry');
    };

    face.addEventListener('pointerenter', enter);
    face.addEventListener('pointermove', move);
    face.addEventListener('pointerleave', leave);
    face.addEventListener('blur', leave);
  };

  /* ------------------------------------------------------------- demo pull */
  const initPack = (pack) => {
    if (!pack || pack.dataset.cstBound) return;
    let data = [];
    try { data = JSON.parse(pack.querySelector('[data-cst-pack-data]').textContent) || []; } catch (_) { data = []; }
    data = data.filter((d) => d && (d.name || d.img));
    const face = pack.querySelector('[data-cst-pack-open]');
    const reveal = pack.querySelector('[data-cst-pack-reveal]');
    const again = pack.querySelector('[data-cst-pack-again]');
    const hint = pack.querySelector('[data-cst-pack-hint]');
    const card = pack.querySelector('[data-cst-pull-card]');
    if (face) initTilt(face); // presentation only — runs even if there are no demo pulls
    if (!face || !reveal || !data.length) return; // no demo pulls → static pack + hint
    pack.dataset.cstBound = '1';

    const revealHint = (pack.dataset.cstRevealHint || '').trim() || DEFAULT_REVEAL_HINT;
    // the hint has two states, and reseal() has to be able to get back to the first
    const idleHint = hint ? hint.textContent : '';
    // each tier's sequence is a different length; the pack carries its own
    const openMs = Math.max(0, parseInt(pack.dataset.cstOpenMs, 10) || OPEN_ANIM_MS);

    const img = pack.querySelector('[data-cst-pull-img]');
    const ph = pack.querySelector('[data-cst-pull-ph]');
    const rarity = pack.querySelector('[data-cst-pull-rarity]');
    const name = pack.querySelector('[data-cst-pull-name]');
    const value = pack.querySelector('[data-cst-pull-value]');

    // Fetch the demo artwork up front. Setting img.src at reveal time means the
    // card arrives empty and the picture lands a beat later, mid-animation.
    data.forEach((d) => { if (d.img) { const i = new Image(); i.src = d.img; } });

    let lastIdx = -1;
    const roll = () => {
      // pick a demo card, but never the same one twice in a row (feels like a fresh pull)
      let idx = Math.floor(Math.random() * data.length);
      if (data.length > 1 && idx === lastIdx) idx = (idx + 1) % data.length;
      lastIdx = idx;
      const pick = data[idx];

      if (img && ph) {
        if (pick.img) { img.src = pick.img; img.alt = pick.name || ''; img.hidden = false; ph.hidden = true; }
        else { img.hidden = true; ph.hidden = false; }
      }
      if (rarity) rarity.textContent = pick.rarity || '';
      if (name) name.textContent = pick.name || '';
      if (value) value.textContent = pick.value || '';
      // re-trigger the reveal animation; skip the forced reflow for reduced-motion users
      if (card && !reduceMotion()) { card.classList.remove('is-in'); void card.offsetWidth; card.classList.add('is-in'); }
    };

    // reveal the card (shared by first open + subsequent re-rolls)
    const showReveal = () => {
      face.hidden = true;
      reveal.hidden = false;
      if (hint) hint.textContent = revealHint;
      roll();
    };

    /* Put the bag back. showReveal() hides the face and nothing ever put it back,
       so an opened pack stayed opened for the life of the page — fine when there
       was one bag you opened once, wrong the moment you can tab away and return
       to it and find the bag gone. The tab switcher calls this. */
    const reseal = () => {
      face.classList.remove('is-opening', 'is-tilting');
      face.style.removeProperty('--cst-rx');
      face.style.removeProperty('--cst-ry');
      face.hidden = false;
      reveal.hidden = true;
      if (card) card.classList.remove('is-in');
      if (hint) hint.textContent = idleHint;
    };
    pack._cstReseal = reseal;

    const open = () => {
      // let the CSS sequence for this tier play, then reveal
      if (openMs > 0 && !reduceMotion()) {
        face.classList.remove('is-tilting'); // tilt and rip both drive the art
        face.classList.add('is-opening');
        setTimeout(() => {
          face.classList.remove('is-opening');
          showReveal();
        }, openMs);
      } else {
        showReveal();
      }
    };

    face.addEventListener('click', open);
    if (again) again.addEventListener('click', roll);
  };

  /* -------------------------------------------------------------- bag tabs
     Swaps which bag is on screen. Every bag is already in the DOM and already
     bound by initPack, so there's nothing to build or tear down here. */
  const initBags = (root) => {
    if (!root || root.dataset.cstBagsBound) return;
    root.dataset.cstBagsBound = '1';
    const tabs = [...root.querySelectorAll('[data-cst-bag-tab]')];
    const slots = [...root.querySelectorAll('[data-cst-bag-slot]')];
    if (!tabs.length || !slots.length) return;

    const show = (tier) => {
      tabs.forEach((t) => t.setAttribute('aria-selected', String(t.dataset.cstBagTab === tier)));
      // settle every slot's visibility FIRST, then touch the packs — so nothing
      // below can leave the group half-switched
      slots.forEach((s) => { s.hidden = s.dataset.cstBagSlot !== tier; });
      const slot = slots.find((s) => s.dataset.cstBagSlot === tier);
      const pack = slot && slot.querySelector('[data-cst-pack]');
      // a bag you come back to should be sealed again, not sitting open
      if (pack && pack._cstReseal) pack._cstReseal();
    };

    tabs.forEach((t) => t.addEventListener('click', () => show(t.dataset.cstBagTab)));

    root.addEventListener('keydown', (e) => {
      const i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      let next = null;
      if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
      if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
      if (!next) return;
      e.preventDefault();
      next.focus();
      show(next.dataset.cstBagTab);
    });
  };

  /* ---------------------------------------------------------- estimator */
  const initEstimator = (est) => {
    if (!est || est.dataset.cstBound) return;
    est.dataset.cstBound = '1';
    const valueInput = est.querySelector('[data-cst-est-value]');
    const rateSelect = est.querySelector('[data-cst-est-rate]');
    const out = est.querySelector('[data-cst-est-out]');
    if (!valueInput || !rateSelect || !out) return;
    const currency = est.dataset.currency || 'USD';
    const calc = () => {
      const v = Math.max(0, parseFloat(valueInput.value) || 0);
      const r = parseFloat(rateSelect.value) || 0;
      out.textContent = money(v * r / 100, currency);
    };
    valueInput.addEventListener('input', calc);
    valueInput.addEventListener('change', calc);
    rateSelect.addEventListener('change', calc);
    calc();
  };

  const scan = (root) => {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-cst-pack]').forEach(initPack);
    scope.querySelectorAll('[data-cst-bags]').forEach(initBags);
    scope.querySelectorAll('[data-cst-estimator]').forEach(initEstimator);
    if (root && root.matches) {
      if (root.matches('[data-cst-pack]')) initPack(root);
      if (root.matches('[data-cst-bags]')) initBags(root);
      if (root.matches('[data-cst-estimator]')) initEstimator(root);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document));
  else scan(document);
  document.addEventListener('shopify:section:load', (e) => scan(e.target || document));
})();